import { effect } from '@preact/signals';
import { heardChapters, currentStreak } from './progress-store.js';
import { bookmarks } from './bookmarks-store.js';
import { highlights } from './highlights-store.js';
import { atlasJumps } from './engagement-store.js';
import { cards } from './memorization-store.js';
import { earnBadge, type BadgeId } from './badges-store.js';

export type BadgeEarnedCallback = (id: BadgeId) => void;

const CHAPTER_COUNTS: Record<string, number> = {
  GEN: 50, EXO: 40, LEV: 27, NUM: 36, DEU: 34, JOS: 24, JDG: 21, RUT: 4,
  '1SA': 31, '2SA': 24, '1KI': 22, '2KI': 25, '1CH': 29, '2CH': 36,
  EZR: 10, NEH: 13, EST: 10, JOB: 42, PSA: 150, PRO: 31, ECC: 12, SNG: 8,
  ISA: 66, JER: 52, LAM: 5, EZK: 48, DAN: 12, HOS: 14, JOL: 3, AMO: 9,
  OBA: 1, JON: 4, MIC: 7, NAM: 3, HAB: 3, ZEP: 3, HAG: 2, ZEC: 14, MAL: 4,
  MAT: 28, MRK: 16, LUK: 24, JHN: 21, ACT: 28, ROM: 16, '1CO': 16, '2CO': 13,
  GAL: 6, EPH: 6, PHP: 4, COL: 4, '1TH': 5, '2TH': 3, '1TI': 6, '2TI': 4,
  TIT: 3, PHM: 1, HEB: 13, JAS: 5, '1PE': 5, '2PE': 3, '1JN': 5, '2JN': 1,
  '3JN': 1, JDE: 1, REV: 22,
};

const NT_BOOKS = ['MAT','MRK','LUK','JHN','ACT','ROM','1CO','2CO','GAL','EPH','PHP','COL','1TH','2TH','1TI','2TI','TIT','PHM','HEB','JAS','1PE','2PE','1JN','2JN','3JN','JDE','REV'];
const OT_BOOKS = ['GEN','EXO','LEV','NUM','DEU','JOS','JDG','RUT','1SA','2SA','1KI','2KI','1CH','2CH','EZR','NEH','EST','JOB','PSA','PRO','ECC','SNG','ISA','JER','LAM','EZK','DAN','HOS','JOL','AMO','OBA','JON','MIC','NAM','HAB','ZEP','HAG','ZEC','MAL'];

function heardAnyBible(heard: Record<string, true>, book: string, chapter: number): boolean {
  const suffix = `:${book}.${chapter}`;
  return Object.keys(heard).some(k => k.endsWith(suffix));
}

function checkWholeBook(heard: Record<string, true>): boolean {
  for (const [book, count] of Object.entries(CHAPTER_COUNTS)) {
    let allHeard = true;
    for (let ch = 1; ch <= count; ch++) {
      if (!heardAnyBible(heard, book, ch)) {
        allHeard = false;
        break;
      }
    }
    if (allHeard) return true;
  }
  return false;
}

function checkTestamentComplete(heard: Record<string, true>, books: string[]): boolean {
  for (const book of books) {
    const count = CHAPTER_COUNTS[book] ?? 0;
    for (let ch = 1; ch <= count; ch++) {
      if (!heardAnyBible(heard, book, ch)) return false;
    }
  }
  return true;
}

function uniqueBibleIds(heard: Record<string, true>): number {
  const ids = new Set<string>();
  for (const key of Object.keys(heard)) {
    const colon = key.indexOf(':');
    if (colon > 0) ids.add(key.slice(0, colon));
  }
  return ids.size;
}

export function bootBadgeEvaluator(onEarned: BadgeEarnedCallback): () => void {
  function tryEarn(id: BadgeId): void {
    if (earnBadge(id)) onEarned(id);
  }

  return effect(() => {
    const heard = heardChapters.value;
    const streak = currentStreak.value;
    const bm = bookmarks.value;
    const hl = highlights.value;
    const jumps = atlasJumps.value;
    const cardMap = cards.value;

    if (Object.keys(heard).length >= 1) tryEarn('first-chapter');
    if (checkWholeBook(heard)) tryEarn('whole-book');

    const ntDone = checkTestamentComplete(heard, NT_BOOKS);
    const otDone = checkTestamentComplete(heard, OT_BOOKS);
    if (ntDone) tryEarn('nt-complete');
    if (otDone) tryEarn('ot-complete');
    if (ntDone && otDone) tryEarn('whole-bible');

    if (streak >= 7) tryEarn('streak-7');
    if (streak >= 30) tryEarn('streak-30');
    if (streak >= 100) tryEarn('streak-100');

    if (bm.length >= 50) tryEarn('bookmark-hoarder');
    if (Object.keys(hl).length >= 20) tryEarn('highlighter');
    if (jumps >= 25) tryEarn('spelunker');
    if (uniqueBibleIds(heard) >= 5) tryEarn('five-translations');
    if (Object.values(cardMap).filter(c => c.reviews >= 3).length >= 5) tryEarn('memorizer');
  });
}
